import React, { useState } from 'react';
import './CreateCampaignModal.css';

/**
 * Simple "new window" modal for creating a campaign.
 */
function CreateCampaignModal({ isOpen, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setFormData({ name: '', description: '' });
    setError('');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Campaign name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim()
      });
      setFormData({ name: '', description: '' });
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to create campaign. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-campaign-modal-overlay" onClick={handleClose}>
      <div className="create-campaign-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="create-campaign-modal-header">
          <h2>🗺️ Create Campaign</h2>
          <button
            className="modal-close-btn"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="create-campaign-form">
          {error && <div className="create-campaign-error">⚠️ {error}</div>}

          <div className="form-group">
            <label htmlFor="name">Campaign Name *</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Ashes of Hustin"
              maxLength={120}
              disabled={isSubmitting}
              required
              autoFocus
            />
            <small className="char-count">{formData.name.length}/120</small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="A short pitch for your players..."
              rows={6}
              maxLength={800}
              disabled={isSubmitting}
            />
            <small className="char-count">{formData.description.length}/800</small>
          </div>

          <div className="create-campaign-modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || !formData.name.trim()}
            >
              {isSubmitting ? 'Creating...' : '✨ Create Campaign'}
            </button>
          </div>
        </form>

        <div className="create-campaign-note">
          <span className="note-icon">ℹ️</span>
          <p>
            Your campaign will be saved to the database and will persist across sessions.
          </p>
        </div>
      </div>
    </div>
  );
}

export default CreateCampaignModal;
